import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";

// Profile cell
function Author({ image, name, id }) {
  const navigate = useNavigate();

  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar
          src={image}
          alt={name}
          size="sm"
          variant="rounded"
          sx={{
            "& img": {
              objectFit: "cover",
              width: "100%",
              height: "100%",
            },
          }}
        />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography
          textTransform="none"
          variant="button"
          fontWeight="medium"
          onClick={() => navigate(`/parents/profile/${id}`)}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: "#1976d2",
              textDecoration: "underline",
            },
          }}
        >
          {name}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

Author.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

// Function to get city and area from coordinates using reverse geocoding
const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    const city = data.address?.city || data.address?.town || data.address?.village || "-";
    const area = data.address?.suburb || data.address?.neighbourhood || "-";
    return { city, area };
  } catch (error) {
    console.error("Error fetching address:", error);
    return { city: "-", area: "-" };
  }
};
const StudentsList = ({ students = [] }) => {
  if (students.length === 0) {
    return (
      <ArgonTypography variant="caption" color="error">
        No Students
      </ArgonTypography>
    );
  }

  const displayStudent = students[0]; // first student
  const extraCount = students.length - 1; // extra students count

  const content = (
    <ArgonBox display="flex" alignItems="center" gap={1}>
      <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
        <ArgonTypography
          textTransform="none"
          sx={{
            backgroundColor: "#f0f0f0",
            padding: "4px 7px",
            borderRadius: "6px",
          }}
          variant="caption"
          fontWeight="medium"
          color="dark"
        >
          {displayStudent} {extraCount > 0 && `+${extraCount}`}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  const tooltipContent = (
    <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
      <ArgonTypography
        variant="caption"
        fontWeight="bold"
        color="white"
        sx={{ display: "block", marginBottom: "8px" }}
      >
        All Students ({students.length}):
      </ArgonTypography>
      <ArgonBox>
        {students.map((student, index) => (
          <ArgonTypography
            key={index}
            variant="caption"
            color="white"
            sx={{ display: "block", marginBottom: "4px" }}
          >
            • {student}
          </ArgonTypography>
        ))}
      </ArgonBox>
    </ArgonBox>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            animation: "fadeInUp 0.3s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px) scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          },
        },
        arrow: {
          sx: {
            color: "rgba(0, 0, 0, 0.9)",
          },
        },
      }}
    >
      <ArgonBox sx={{ cursor: "pointer" }}>{content}</ArgonBox>
    </Tooltip>
  );
};

const DEFAULT_PAGE_LIMIT = 100;
const CLASS_SESSION_LIST_STATUSES = "CREATED,SCHEDULED,ONGOING,ON_HOLD,COMPLETED,CANCELLED";
const ACTIVE_CLASS_STATUSES = ["ONGOING", "SCHEDULED", "CREATED"];

const getClassSessionParentStatus = (session = {}) => {
  const sessionStatus = session.status;
  const totalClasses = Number(session.totalClasses || session.totalNoOfClasses || 0);
  const completedClasses = Number(session.completedClasses || 0);
  const cycles = Array.isArray(session.cycles) ? session.cycles : [];
  const hasCycles = cycles.length > 0;
  const allCyclesCompleted = hasCycles && cycles.every((cycle) => cycle?.status === "COMPLETED");

  if (ACTIVE_CLASS_STATUSES.includes(sessionStatus)) {
    if (
      sessionStatus === "ONGOING" &&
      ((totalClasses > 0 && completedClasses >= totalClasses) || allCyclesCompleted)
    ) {
      return "COMPLETED";
    }

    return "ACTIVE";
  }

  if (sessionStatus === "ON_HOLD") return "HOLD";
  if (
    sessionStatus === "COMPLETED" ||
    (totalClasses > 0 && completedClasses >= totalClasses) ||
    allCyclesCompleted
  ) {
    return "COMPLETED";
  }

  return null;
};

const getStatusPriority = (status) =>
  ({
    ACTIVE: 4,
    HOLD: 3,
    COMPLETED: 2,
  })[status] || 0;

const getTotalCount = (responseData) =>
  responseData?.pagination?.total ||
  responseData?.pagination?.totalSessions ||
  responseData?.total ||
  responseData?.count ||
  0;

const getPageItems = (responseData) => (Array.isArray(responseData?.data) ? responseData.data : []);

const fetchAllPages = async (url, params = {}) => {
  const firstResponse = await axios.get(url, {
    params: { ...params, page: 1, limit: DEFAULT_PAGE_LIMIT },
  });
  const firstData = firstResponse.data;
  const firstItems = getPageItems(firstData);
  const total = getTotalCount(firstData) || firstItems.length;
  const totalPages = Math.ceil(total / DEFAULT_PAGE_LIMIT);

  if (totalPages <= 1) {
    return firstResponse;
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      axios.get(url, {
        params: { ...params, page: index + 2, limit: DEFAULT_PAGE_LIMIT },
      })
    )
  );

  return {
    ...firstResponse,
    data: {
      ...firstData,
      data: [
        ...firstItems,
        ...remainingResponses.flatMap((response) => getPageItems(response.data)),
      ],
    },
  };
};
StudentsList.propTypes = {
  students: PropTypes.arrayOf(PropTypes.string),
};

const useParentsData = (handleDeleteClick, refresh, activeTab = "All", options = {}) => {
  const enabled = options.enabled !== false;
  const includeClassSessions = options.includeClassSessions !== false;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    All: 0,
    ACTIVE: 0,
    HOLD: 0,
    COMPLETED: 0,
    CANCELLED: 0,
    DISCONTINUED: 0,
    SME: 0,
    COMBINED: 0,
    SEPARATE: 0,
  });
  const columns = [
    { name: "sno", align: "center" },
    { name: "profile", align: "left" },
    { name: "students", align: "left" },
    { name: "class Name", align: "left" },
    { name: "register Date", align: "left" },
    { name: "area", align: "left" },
    { name: "city", align: "left" },
    { name: "action", align: "center" },
  ];

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch all parents first
        const parentsRes = await fetchAllPages(`${baseURL}/parent`);

        if (!parentsRes.data.success) {
          toast.error("Failed to load parents");
          setRows([]);
          return;
        }

        const parents = parentsRes.data.data || [];

        if (parents.length === 0) {
          setRows([]);
          return;
        }

        // Fetch all students to get their info for each parent
        const studentsRes = await fetchAllPages(`${baseURL}/student`);

        const students = studentsRes.data.success ? studentsRes.data.data || [] : [];

        const classSessions = includeClassSessions
          ? await fetchAllPages(`${baseURL}/classes/sessions/all`, {
              status: CLASS_SESSION_LIST_STATUSES,
            }).then((classSessionsRes) =>
              classSessionsRes.data.success ? classSessionsRes.data.data || [] : []
            )
          : [];

        // Create a map of parentId -> class session status
        const parentStatusMap = {};
        const cancelledParentIds = new Set();
        classSessions.forEach((session) => {
          const parentId = session?.parentId?._id || session?.parentId;
          if (parentId) {
            const parentIdStr = String(parentId);
            if (session.status === "CANCELLED") {
              cancelledParentIds.add(parentIdStr);
            }
            const mappedStatus = getClassSessionParentStatus(session);
            if (!mappedStatus) return;

            if (
              !parentStatusMap[parentIdStr] ||
              getStatusPriority(mappedStatus) > getStatusPriority(parentStatusMap[parentIdStr])
            ) {
              parentStatusMap[parentIdStr] = mappedStatus;
            }
          }
        });

        // Create a map of parentId -> class type (Combined/SME/Separate)
        const parentClassTypeMap = {};
        classSessions.forEach((session) => {
          const parentId = session?.parentId?._id || session?.parentId;
          if (parentId) {
            const parentIdStr = String(parentId);
            const classType = session.classType;
            const studentCount = session.students?.length || 0;

            // Determine class type for parent
            if (classType === "SME") {
              parentClassTypeMap[parentIdStr] = "SME";
            } else if (classType === "COMBINED" || studentCount > 1) {
              if (parentClassTypeMap[parentIdStr] !== "SME") {
                parentClassTypeMap[parentIdStr] = "COMBINED";
              }
            } else if (studentCount === 1) {
              if (!parentClassTypeMap[parentIdStr]) {
                parentClassTypeMap[parentIdStr] = "SEPARATE";
              }
            }
          }
        });

        // Create a map of parentId -> students info
        const parentStudentsMap = {};
        students.forEach((student) => {
          const parentId = student?.parentId?._id || student?.parentId;

          if (parentId) {
            // Convert to string to ensure matching
            const parentIdStr = String(parentId);

            if (!parentStudentsMap[parentIdStr]) {
              parentStudentsMap[parentIdStr] = {
                students: [],
                classes: [],
              };
            }
            parentStudentsMap[parentIdStr].students.push(student?.name || "-");
            if (student?.class?.name) {
              parentStudentsMap[parentIdStr].classes.push(student.class.name);
            }
          }
        });

        // Format rows for all parents with their status
        const allFormattedRows = parents.map((parent) => {
          const parentIdStr = String(parent._id);
          const studentInfo = parentStudentsMap[parentIdStr] || {
            students: [],
            classes: [],
          };

          // Determine parent status
          let parentStatus = "All";
          if (parent.isDeleted === true || parent.isDeleted === "true") {
            parentStatus = "DISCONTINUED";
          } else {
            parentStatus = parentStatusMap[parentIdStr] || "All";
          }

          // Determine parent class type from student count.
          // 2+ students under one parent are shown as Combined; exactly 1 as Separate.
          const parentClassType =
            studentInfo.students.length > 1
              ? "COMBINED"
              : studentInfo.students.length === 1
                ? "SEPARATE"
                : null;

          return {
            parentId: parent._id,
            crmSearchEntity: { fullName: parent.fullName, phone: parent.phone, alternatePhone: parent.alternatePhone, students: studentInfo.students },
            parentPhone: parent.phone || "",
            parentStatus: parentStatus,
            hasCancelledClass: cancelledParentIds.has(parentIdStr),
            parentClassType: parentClassType,
            sno: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                -
              </ArgonTypography>
            ),

            profile: (
              <Author image={parent.profileImage} name={parent.fullName || "-"} id={parent._id} />
            ),

            students: <StudentsList students={studentInfo.students} />,

            "class Name": (
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                sx={{ padding: "4px" }}
              >
                {studentInfo.classes.length > 0 ? studentInfo.classes.join(", ") : "-"}
              </ArgonTypography>
            ),

            "register Date": (
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                sx={{ padding: "4px" }}
              >
                {parent.createdAt ? new Date(parent.createdAt).toLocaleDateString() : "-"}
              </ArgonTypography>
            ),

            city: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {(() => {
                  const addressStr = parent.address || "";
                  const cityMatch = addressStr.match(/City:\s*(.*?)(?=,\s*[a-zA-Z\/]+:|$)/i);
                  if (cityMatch) return cityMatch[1].trim();
                  return parent.city || "-";
                })()}
              </ArgonTypography>
            ),

            area: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {(() => {
                  const addressStr = parent.address || "";
                  const areaMatch = addressStr.match(/Area:\s*(.*?)(?=,\s*[a-zA-Z\/]+:|$)/i);
                  if (areaMatch) return areaMatch[1].trim();
                  return parent.area || "-";
                })()}
              </ArgonTypography>
            ),

            status: (
              <ArgonBadge
                variant="gradient"
                badgeContent="Active"
                color="success"
                size="xs"
                container
              />
            ),

            action: (
              <ArgonBox display="flex" alignItems="center" gap={0}>
                {/* View */}
                <Button
                  variant="text"
                  size="large"
                  component={Link}
                  to={`/parents/profile/${parent._id}`}
                  sx={{ minWidth: "12px", p: 0 }}
                >
                  <VisibilityIcon color="primary" />
                </Button>

                {/* Delete */}
                <Button
                  variant="text"
                  size="large"
                  sx={{ minWidth: "32px", p: 0 }}
                  onClick={() => handleDeleteClick(parent._id)}
                >
                  <DeleteIcon color="error" />
                </Button>
              </ArgonBox>
            ),
          };
        });

        // Calculate counts for each status
        const statusCounts = {
          All: allFormattedRows.length,
          ACTIVE: allFormattedRows.filter((row) => row.parentStatus === "ACTIVE").length,
          HOLD: allFormattedRows.filter((row) => row.parentStatus === "HOLD").length,
          COMPLETED: allFormattedRows.filter((row) => row.parentStatus === "COMPLETED").length,
          CANCELLED: allFormattedRows.filter((row) => row.hasCancelledClass).length,
          DISCONTINUED: allFormattedRows.filter((row) => row.parentStatus === "DISCONTINUED")
            .length,
          SME: allFormattedRows.filter((row) => row.parentClassType === "SME").length,
          COMBINED: allFormattedRows.filter((row) => row.parentClassType === "COMBINED").length,
          SEPARATE: allFormattedRows.filter((row) => row.parentClassType === "SEPARATE").length,
        };

        setCounts(statusCounts);

        // Filter rows based on active tab
        let filteredRows = allFormattedRows;
        if (activeTab === "CANCELLED") {
          filteredRows = allFormattedRows.filter((row) => row.hasCancelledClass);
        } else if (activeTab === "SME") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "SME");
        } else if (activeTab === "COMBINED") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "COMBINED");
        } else if (activeTab === "SEPARATE") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "SEPARATE");
        } else if (activeTab !== "All") {
          filteredRows = allFormattedRows.filter((row) => row.parentStatus === activeTab);
        }

        // Re-index the filtered rows
        const finalRows = filteredRows.map((row, index) => ({
          ...row,
          sno: (
            <ArgonTypography variant="caption" fontWeight="medium" color="text">
              {index + 1}
            </ArgonTypography>
          ),
        }));

        setRows(finalRows);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching parents:", err);
        setLoading(false);
        toast.error(err.response?.data?.error || "Failed to load parents");
        setRows([]);
        setCounts({
          All: 0,
          ACTIVE: 0,
          HOLD: 0,
          COMPLETED: 0,
          CANCELLED: 0,
          DISCONTINUED: 0,
          SME: 0,
          COMBINED: 0,
          SEPARATE: 0,
        });
      }
    };

    fetchData();
  }, [refresh, activeTab, enabled, includeClassSessions]);

  return { columns, rows, counts, loading };
};

export default useParentsData;
